// "(keeping)" annotation for the Compatible systems kept-pairings summary
// (AHA-6681, Geoff 2026-07-31): each kept-column row label segment that
// corresponds to a KEPT component's class gets a "(keeping)" suffix.
//
// Class membership is decided STRUCTURALLY from the row's system-type `code`
// (the composition-encoded SystemTypeCompatibilityCodes grammar —
// `<outdoor>_<coil|ah>_<fuel>-<blower>[_hk|_lp]`, see
// migrations/notes/aha-6681-system-type-code-mapping.md), never by guessing
// from the label text. The label (DB-authored, no machine mapping to code
// parts) is only consulted to PLACE the suffix: a slot-vocabulary match on
// the ' + '-separated segments, gated to classes the code parse confirmed
// AND the kept set matched. That gate is what keeps e.g. a kept split
// furnace from ever annotating the "Furnace" inside a packaged label like
// "Packaged Heat Pump / NG Furnace" (packaged codes parse to a packaged
// class only).

// One physical component class per code part / kept component type. Standard
// and Next Gen are distinct classes on purpose: a kept standard heat pump
// must never annotate a "Next Gen Heat Pump" segment.
export type ComponentClass =
  | 'condenser'
  | 'heatPump'
  | 'nextGenCondenser'
  | 'nextGenHeatPump'
  | 'evaporatorCoil'
  | 'nextGenEvaporatorCoil'
  | 'airHandler'
  | 'nextGenAirHandler'
  | 'furnace'
  | 'modularBlower'
  | 'packaged';

export type CodeFurnaceKind = 'furnace' | 'mobile-home-furnace';

const SINGLE_TOKEN_CLASSES: Record<string, ComponentClass | undefined> = {
  ac: 'condenser',
  hp: 'heatPump',
  an: 'nextGenCondenser',
  ec: 'evaporatorCoil',
  en: 'nextGenEvaporatorCoil',
  ah: 'airHandler',
  dh: 'nextGenAirHandler',
  mb: 'modularBlower',
  pg: 'packaged',
  pd: 'packaged',
  ph: 'packaged',
  pa: 'packaged',
  pt: 'packaged'
};

// Ducted-furnace fuel prefixes pair with the `uf` token (`ng-df_uf` is ONE
// furnace part — a naive split('_') mis-tokenizes it); mobile-home furnaces
// are a single `<fuel>-mf` token.
const DUCTED_FURNACE_FUEL = /^(ng|lp|o)-df$/;
const MOBILE_HOME_FURNACE = /^(ng|lp|o|e)-mf$/;

// The Fuel filter chip vocabulary (AHA-6796): the grammar's combustion-fuel
// prefixes plus the 'e' Electric bucket every non-combustion code lands in.
export type DuctedFuelToken = 'ng' | 'lp' | 'o' | 'e';

// Packaged codes with a gas furnace side (mapping-doc labels "Packaged Gas
// ..." / "Packaged Dual Fuel ..."): NG unless the LP-conversion suffix says
// otherwise. The other packaged codes (ph/pa/pt) are electric.
const PACKAGED_NG_TOKENS = new Set(['pg', 'pd']);

// TOTAL fuel classification of a system-type compatibility code (AHA-6796
// Fuel filter chips) — every code lands under exactly one chip. Same token
// grammar as systemTypeCodeClasses (no second parser): a `<fuel>-df`/`-mf`
// furnace part names its fuel; the standalone `lp` LP-conversion suffix
// (`pg_lp`, `pd_lp`) wins over everything; bare `pg`/`pd` are NG; every
// code with no combustion-fuel token (heat pumps, air handlers,
// cooling-only, packaged electric) buckets under 'e' Electric.
export function systemTypeCodeFuel(code: string): DuctedFuelToken {
  const tokens = code.split('_');
  let fuel: DuctedFuelToken | null = null;
  for (const token of tokens) {
    if (token === 'lp') return 'lp';
    const furnace =
      DUCTED_FURNACE_FUEL.exec(token) ?? MOBILE_HOME_FURNACE.exec(token);
    if (furnace) {
      fuel = furnace[1] as DuctedFuelToken;
    } else if (fuel === null && PACKAGED_NG_TOKENS.has(token)) {
      fuel = 'ng';
    }
  }
  return fuel ?? 'e';
}

// Parses a system-type compatibility code into its component classes.
// Accessory/fuel-variant suffixes (`hk`, standalone `lp`) and unknown tokens
// carry no class and are skipped.
export function systemTypeCodeClasses(code: string): Set<ComponentClass> {
  const tokens = code.split('_');
  const classes = new Set<ComponentClass>();
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token === 'sc' && tokens[i + 1] === 'uc') {
      classes.add('nextGenHeatPump');
      i += 1;
    } else if (DUCTED_FURNACE_FUEL.test(token) && tokens[i + 1] === 'uf') {
      classes.add('furnace');
      i += 1;
    } else if (MOBILE_HOME_FURNACE.test(token)) {
      classes.add('furnace');
    } else {
      const cls = SINGLE_TOKEN_CLASSES[token];
      if (cls) {
        classes.add(cls);
      }
    }
  }
  return classes;
}

export function systemTypeCodeFurnaceKind(
  code: string
): CodeFurnaceKind | null {
  const tokens = code.split('_');
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (DUCTED_FURNACE_FUEL.test(token) && tokens[i + 1] === 'uf') {
      return 'furnace';
    }
    if (MOBILE_HOME_FURNACE.test(token)) {
      return 'mobile-home-furnace';
    }
  }
  return null;
}

export function systemTypeCodePackagedToken(code: string): string | null {
  for (const token of code.split('_')) {
    if (SINGLE_TOKEN_CLASSES[token] === 'packaged') {
      return token;
    }
  }
  return null;
}

// Existing-equipment component `type` display strings (the
// ExistingSystemComponentSchema discriminants — same vocabulary as
// COMPONENT_TYPE_TO_POSITION in utils/existing-equipment). Unmapped types
// (ductless, self-contained/packaged, PTAC, unknown) contribute nothing.
// The GUI has ONE furnace type, so it matches both ducted (`*-df_uf`) and
// mobile-home (`*-mf`) furnace code parts.
const COMPONENT_TYPE_TO_CLASS: Record<string, ComponentClass> = {
  'Split System Condenser': 'condenser',
  'Split System Heat Pump': 'heatPump',
  'Next-Gen Condenser': 'nextGenCondenser',
  'Next-Gen Heat Pump': 'nextGenHeatPump',
  'Split System Evaporator Coil': 'evaporatorCoil',
  'Next-Gen Evaporator Coil': 'nextGenEvaporatorCoil',
  'Split System Air Handler': 'airHandler',
  'Next-Gen Air Handler': 'nextGenAirHandler',
  'Split System Furnace': 'furnace',
  'Split System Modular Blower': 'modularBlower'
};

// Placement-only slot vocabulary (see module comment): matches a label
// segment to a code-confirmed class. Standard classes exclude Next Gen
// segments and vice versa.
const NEXT_GEN = /next[\s-]?gen/i;
const SEGMENT_MATCHERS: Record<ComponentClass, (segment: string) => boolean> = {
  condenser: (s) => /(condenser|air conditioner)/i.test(s) && !NEXT_GEN.test(s),
  heatPump: (s) => /heat pump/i.test(s) && !NEXT_GEN.test(s),
  nextGenCondenser: (s) =>
    /(condenser|air conditioner)/i.test(s) && NEXT_GEN.test(s),
  nextGenHeatPump: (s) => /heat pump/i.test(s) && NEXT_GEN.test(s),
  evaporatorCoil: (s) => /coil/i.test(s) && !NEXT_GEN.test(s),
  nextGenEvaporatorCoil: (s) => /coil/i.test(s) && NEXT_GEN.test(s),
  airHandler: (s) => /air handler/i.test(s) && !NEXT_GEN.test(s),
  nextGenAirHandler: (s) => /air handler/i.test(s) && NEXT_GEN.test(s),
  furnace: (s) => /furnace/i.test(s),
  modularBlower: (s) => /blower/i.test(s),
  // No kept component type maps to a packaged class, and packaged labels
  // must never be annotated.
  packaged: () => false
};

export type AnnotatedLabelSegment = { text: string; keeping: boolean };

// Splits `label` into its ' + ' segments with a `keeping` flag on each
// segment that corresponds to a kept component's class. Returns null when
// nothing matched (nothing kept maps, no class intersection, or no segment
// placement) — the caller renders the plain label exactly as before.
export function annotateKeptSystemTypeLabel(
  label: string,
  code: string,
  keptComponentTypes: string[]
): AnnotatedLabelSegment[] | null {
  const keptClasses = new Set(
    keptComponentTypes
      .map((type) => COMPONENT_TYPE_TO_CLASS[type])
      .filter((cls): cls is ComponentClass => cls !== undefined)
  );
  if (keptClasses.size === 0) return null;

  const matchedClasses = Array.from(systemTypeCodeClasses(code)).filter((cls) =>
    keptClasses.has(cls)
  );
  if (matchedClasses.length === 0) return null;

  const segments = label.split(' + ').map((text) => ({
    text,
    keeping: matchedClasses.some((cls) => SEGMENT_MATCHERS[cls](text))
  }));
  return segments.some((segment) => segment.keeping) ? segments : null;
}

// Display-title variant of the annotation (AHA-6681 "Customer's <equipment>"):
// kept segments render as "Customer's <segment>" instead of a "(keeping)"
// suffix. Reuses annotateKeptSystemTypeLabel verbatim, so it only ever
// REWRITES existing label segments — a null annotation (packaged code,
// unparsable code, no kept-class match, no segment placement) returns the
// raw label unchanged.
export function formatKeptSystemTypeLabel(
  label: string,
  code: string,
  keptComponentTypes: string[]
): string {
  const segments = annotateKeptSystemTypeLabel(label, code, keptComponentTypes);
  if (!segments) return label;
  return segments
    .map((segment) =>
      segment.keeping ? `Customer's ${segment.text}` : segment.text
    )
    .join(' + ');
}
